// @flow
import * as React from 'react';
// $FlowIssue
import { useEffect, useState } from 'react';
import { upload } from './cloudinaryApi';
import styles from './MediaThumbnail.module.scss';
import { XIcon } from 'components/Icons/XIcon';
import PwrCloudinary from 'lib/pwr-cloudinary';
import classnames from 'classnames';
import {
  type Resource,
  type UploadResponse,
  ResourceTypes,
} from './ResourceTypes';

type Props = {
  item: Resource,
  onDelete: string => void,
  onUpload?: ?(string, UploadResponse | Error) => void,
};
export function MediaThumbnail(props: Props) {
  const { item, onUpload, onDelete } = props;

  if (ResourceTypes.isUploadable(item)) {
    return <UploadFile item={item} onUpload={onUpload} />;
  }

  // $FlowFixMe
  const { url = '', resourceType } = item;
  const thumbnailUrl = createThumbnailURL(url, resourceType);

  return (
    <Container>
      <img src={thumbnailUrl} alt={item.id} className={styles.img} />
      <MediaTypeIcon type={resourceType} />
      <RemoveButton itemId={item.id} onClick={onDelete} />
    </Container>
  );
}

export function ExistingMediaThumbnail(props: {
  ugc: *,
  onDelete: UgcId => void,
}) {
  const {
    ugc: {
      ugc_id,
      ugctype,
      urlSet: { thumbnail },
    },
    onDelete,
  } = props;
  return (
    <Container>
      <img src={thumbnail} alt={ugc_id} className={styles.img} />
      <MediaTypeIcon type={ugctype} />
      <RemoveButton itemId={ugc_id} onClick={onDelete} />
    </Container>
  );
}

function RemoveButton({
  itemId,
  onClick,
}: {
  itemId: string,
  onClick: string => void,
}) {
  return (
    <button
      onClick={() => onClick(itemId)}
      type="button"
      className={styles.removeButton}
      aria-label={`Remove ${itemId}`}
    >
      <XIcon className={styles.removeButtonIcon} />
    </button>
  );
}

function MediaTypeIcon({ type: _type }: { type: string }) {
  const type = _type.toLowerCase();

  return (
    <div className={styles.resourceTypeIcon}>
      <i
        className={classnames('fa white', {
          'fa-camera': type === 'image',
          'fa-video-camera': type === 'video',
        })}
        aria-hidden={true}
      />
    </div>
  );
}

function Container(props) {
  return <div className={styles.mediaThumbnail}>{props.children}</div>;
}

// $$FlowFixMe
const UploadFile = React.memo(function UploadFile(props: {
  item: Resource,
  onUpload?: (string, UploadResponse | Error) => void,
}) {
  const { item, onUpload } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(
    () => {
      if (typeof onUpload !== 'function') {
        return;
      }

      let current = true;
      const uploadFileToCloudinary = async () => {
        try {
          const [file, resourceType] = ResourceTypes.getUploadProps(item);
          const response = await upload(file, resourceType);
          current && setIsLoading(false);
          onUpload(item.id, response);
        } catch (err) {
          setIsLoading(false);
          current && setError(true);
          onUpload(item.id, err);
        }
      };

      uploadFileToCloudinary();
      return () => (current = false);
    },
    [item]
  );

  const status = isLoading ? (
    <i
      className="fa fa-cloud-upload black-30"
      aria-label={`Uploading ${item.id}`}
    />
  ) : error ? (
    <i
      className="fa fa-exclamation-triangle dark-red"
      aria-label={`Upload of ${item.id} Failed`}
    />
  ) : null;

  return (
    <Container>
      <div className="flex w-100 h-100 items-center justify-center f3">
        {status}
      </div>
    </Container>
  );
},
(prevProps, nextProps) => prevProps.item.id === nextProps.item.id);

function createThumbnailURL(src, contentType) {
  if (contentType === 'image') {
    return PwrCloudinary.libraryUrlSet(src).thumbnail;
  }

  if (contentType === 'video') {
    const accessType = PwrCloudinary.isYoutubeVideo(src) ? 'youtube' : 'upload';

    const { thumbnail } =
      accessType === 'youtube'
        ? PwrCloudinary.youtubeUrlSet(src)
        : PwrCloudinary.libraryVideoUrlSet(src);

    return thumbnail;
  }

  throw new Error(`Unknown content type ${contentType}`);
}



// WEBPACK FOOTER //
// ./src/components/ContentSection/__future__/MediaUpload/MediaThumbnail.js